import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CountryFlag from './CountryFlag';
import { NATIONALITIES } from '../utils/nationalities';
import './NationalitySelect.css';

const defaultOptions = NATIONALITIES.map((item) => ({
  code: item.code,
  name: item.name,
  value: item.code,
}));

const NationalitySelect = ({
  id,
  value = '',
  onChange,
  options = defaultOptions,
  allowEmpty = false,
  emptyLabel = 'Seleccionar',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((option) => option.value === value);

  const placeMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 240 && rect.top > spaceBelow;
    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 10000,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    placeMenu();
    const close = (event) => {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const reposition = () => placeMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const pick = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className={`nationality-select ${className}`.trim()} ref={rootRef}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className="nationality-select__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => {
          if (!open) placeMenu();
          setOpen((current) => !current);
        }}
      >
        {selected ? (
          <>
            <CountryFlag code={selected.code} title={selected.name} />
            <span className="nationality-select__label">{selected.name}</span>
          </>
        ) : (
          <span className="nationality-select__placeholder">{emptyLabel}</span>
        )}
        <span className="nationality-select__chevron" aria-hidden="true">▾</span>
      </button>
      {open && createPortal(
        <ul ref={menuRef} className="nationality-select__menu" style={menuStyle} role="listbox">
          {allowEmpty && (
            <li>
              <button type="button" role="option" aria-selected={!value} onClick={() => pick('')}>
                <span>{emptyLabel}</span>
              </button>
            </li>
          )}
          {options.map((option) => (
            <li key={option.code}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => pick(option.value)}
              >
                <CountryFlag code={option.code} title={option.name} />
                <span>{option.name}</span>
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
};

export default NationalitySelect;
